<template>
  <section
      class="io-section"
      :class="sectionClass"
      :style="sectionStyles"
      ref="section"
  >
    <Observer
        v-if="props.wave !== 'none'"
        :class="{ 'wave-bottom': waveMarginBottom }"
        @resize="onWaveResize"
    >
      <IoWave
          :mask-id="waveId"
          class="wave"
          :wave-type="props.wave"
          :background="background"
          :wave-mirrored="props.waveMirrored"
          :wave-negative="props.waveNegative"
      />
    </Observer>
    <LazyBackgroundMedia
        class="io-section__background"
        :class="backgroundClasses"
        :media-id="mediaId"
        :style="maskStyles"
    />
    <div
        :id="anchorId"
        class="io-section__content"
    >
      <slot></slot>
    </div>
    <div
        v-if="loading"
        class="io-section__loader"
    >
      <FontAwesomeIcon
          :icon="faSpinner"
          class="fa-pulse"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import {
  SectionPropsDefaults,
  SectionPropsInterface,
} from '@/data/Section';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';
import {
  computed,
  ref,
} from 'vue';
import IoWave from '@/components/IoWave.vue';
import { getRandomId } from '@/helpers';
import Observer from '@/components/Observer.vue';

library.add(faSpinner);

const props = withDefaults(defineProps<SectionPropsInterface>(), SectionPropsDefaults);

const waveId = getRandomId('wave-');

const maskStyles = computed(() => {
  let svgMaskUrl = '';

  if (props.wave !== 'none') {
    if (props.wave === 'wave') {
      if (props.waveNegative && !props.waveMirrored) {
        svgMaskUrl = `${window.ioappConfig.themeUri}/public/img/wave/WelleNegativ.webp`;
      } else if (props.waveNegative && props.waveMirrored) {
        svgMaskUrl = `${window.ioappConfig.themeUri}/public/img/wave/WelleNegativGespiegelt.webp`;
      } else if (!props.waveNegative && props.waveMirrored) {
        svgMaskUrl = `${window.ioappConfig.themeUri}/public/img/wave/WelleGespiegelt.webp`;
      } else {
        svgMaskUrl = `${window.ioappConfig.themeUri}/public/img/wave/Welle.webp`;
      }
    } else if (props.wave === 'double-wave') {
      if (props.waveNegative && !props.waveMirrored) {
        svgMaskUrl = `${window.ioappConfig.themeUri}/public/img/wave/DoppelwelleNegativ.webp`;
      } else if (props.waveNegative && props.waveMirrored) {
        svgMaskUrl = `${window.ioappConfig.themeUri}/public/img/wave/DoppelwelleNegativGespiegelt.webp`;
      } else if (!props.waveNegative && props.waveMirrored) {
        svgMaskUrl = `${window.ioappConfig.themeUri}/public/img/wave/DoppelwelleGespiegelt.webp`;
      } else {
        svgMaskUrl = `${window.ioappConfig.themeUri}/public/img/wave/WelleDoppelt.webp`;
      }
    }
  }

  return {
    mask: `url(#${waveId}) top/auto no-repeat, linear-gradient(#fff 0 0)`,
    '-webkit-mask': `url(${svgMaskUrl}) top/auto no-repeat, linear-gradient(#fff 0 0)`,
    '-webkit-mask-composite': 'destination-out',
    'mask-composite': 'exclude',
    'mask-size': 'calc(100% + 6px) auto',
    'mask-position': 'center -3px, 0% 0%',
  };
});

const waveMarginBottom = computed<boolean>(() => {
  if (props.wave !== 'none') {
    if (props.wave === 'wave') {
      return (!props.waveNegative && props.waveMirrored)
        || (!props.waveNegative && !props.waveMirrored);
    }
  }

  return false;
});

const backgroundClasses = computed<string[]>(() => {
  const classes: string[] = [];

  classes.push(`bg-${props.background}`);

  if (props.gradient !== 'none') {
    classes.push(`gradient-${props.gradient}`);
  }

  return classes;
});

const section = ref();
const sectionStyles = ref<Record<string, string>>({});

const onWaveResize = ({ height }: Record<string, any>) => {
  sectionStyles.value.marginTop = `-${height || 0}px`;

  if (
    section.value
      && section.value.previousSibling
      && section.value.previousSibling.style !== undefined
      && props.wave !== 'none'
  ) {
    const percentageValue = parseInt(props.wavePaddingPercentage || '0', 10) / 100;

    section.value.previousSibling.style.setProperty('--wave-padding-bottom', `${height || 0}px`);
    section.value.previousSibling.style.setProperty('--wave-padding-bottom-percentage', percentageValue);
  } else if (
    section.value
      && section.value.previousSibling
      && section.value.previousSibling.style !== undefined
  ) {
    section.value.previousSibling.style.removeProperty('--wave-padding-bottom');
    section.value.previousSibling.style.removeProperty('--wave-padding-bottom-percentage');
  }
};

const sectionClass = computed<string[]>(() => {
  const sectionClasses: string[] = [];

  if (!props.paddingTop) {
    sectionClasses.push('pt-0');
  }

  if (!props.paddingBottom) {
    sectionClasses.push('pb-0');
  }

  if (props.wave !== 'none') {
    sectionClasses.push('has-wave');
  }

  if (props.loading) {
    sectionClasses.push('loading');
  }

  return sectionClasses;
});
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.io-section {
  position: relative;
  background: transparent;
  z-index: 3;
  pointer-events: none;

  &:not(.has-wave):before, &:after {
    @include responsive-size-lg(100);
    content: '';
    display: block;
    padding-top: clamp(1em, calc(var(--wave-padding-bottom, 1em) * var(--wave-padding-bottom-percentage, 0)), 9999px);
  }

  &.has-wave {
    .app-wave {
      @include responsive-size-lg(100);
      display: block;
      margin-bottom: 1em;
      max-width: 100%;
      opacity: 0;
      pointer-events: none;
    }

    .wave-bottom {
      margin-bottom: -17rem;
    }

    &.pt-0 .app-wave {
      margin-bottom: 0;
    }
  }

  &.pt-0:before {
    display: none;
  }

  &.pb-0:after {
    display: none;
  }

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;

    > div {
      background-position: center;
      background-size: cover;
    }

    &.bg {
      &-white {
        background-color: #FFFFFF;
      }

      &-secondary {
        background-color: $body-bg-secondary;
      }

      &-power {
        background-color: $primary;
      }

      &-gas {
        background-color: $primary;
      }

      &-heat {
        background-color: $primary;
      }

      &-energy-solutions {
        background-color: $primary;
      }

      &-glas-fiber, &-internet {
        background-color: $primary;
      }

      &-e-mobility {
        background-color: $primary;
      }

      &-gradient {
        background: transparent linear-gradient(270deg, #4BBECF 0%, #AECC53 100%) 0% 0% no-repeat padding-box !important;
      }
    }
  }

  &__content {
    position: relative;
    pointer-events: all;
  }

  &.loading {
    position: relative;

    .io-section__content {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);
    @include responsive-size-xl(60);
    color: $body-color;
  }
}

</style>
